import React, { useState, useRef, useEffect } from "react";
import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import instance from "../../services/services";
import { MAKING_SUPPORT_REGISTER_URL, MAKING_SUPPORT_EDIT_URL, MAKING_SUPPORT_APPLY_DETAIL_URL } from "../../utils/urls";
import PopupLoginError from "../../components/Popup/LoginError";
import { useLanguage } from "../../hooks/useLanguage";

function SupportForm(){
  const { t } = useLanguage();
  const { myInfo } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  
  // URL 쿼리 파라미터에서 apply_seq 추출
  const queryParams = new URLSearchParams(location.search);
  const apply_seq = queryParams.get('apply_seq');
  const isEdit = !!apply_seq;
  
  // location.state에서 값들 가져오기
  const { from, editData } = location.state || {};
  const isFromMyPage = from === 'mypage';
  
  // 폼 상태 관리
  const [formData, setFormData] = useState({
    apply_phone: myInfo?.mem_phone || '',
    apply_email: myInfo?.email || '',
    apply_company: '',
    apply_director: '',
    apply_cinematographer: '',
    apply_workname: '',
    apply_workcontents: '',
    apply_contents: '',
    youtube_url: '',
    ucc_url: '',
  });
  
  // 파일 상태 관리
  const [uploadMainImage, setUploadMainImage] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [uploadSubImages, setUploadSubImages] = useState([]);
  const [subImagesPreview, setSubImagesPreview] = useState([]);
  const [attachFiles, setAttachFiles] = useState([null, null]);
  const [attachFileNames, setAttachFileNames] = useState(['첨부 파일', '첨부 파일']);
  
  // 파일 인풋 레퍼런스
  const mainImageInputRef = useRef(null);
  const subImageInputRef = useRef(null);
  const fileInput1Ref = useRef(null);
  const fileInput2Ref = useRef(null);
  
  // 수정 모드일 경우 기존 데이터 불러오기
  useEffect(() => {
    if (isEdit && editData) {
      // location.state로 전달받은 데이터 사용
      
      // 폼 데이터 설정
      setFormData({
        apply_phone: editData.cellphone || '',
        apply_email: editData.email || '',
        apply_company: editData.business_name || '',
        apply_director: editData.director || '',
        apply_cinematographer: editData.cinematographer || '',
        apply_workname: editData.work_title || '',
        apply_workcontents: editData.work_description || '',
        apply_contents: editData.apply_content || '',
        youtube_url: editData.youtube_url || '',
        ucc_url: editData.ucc_url || '',
      });
      
      // 대표 이미지 데이터 처리 (main_image 배열 사용)
      if (editData.main_image && editData.main_image.length > 0) {
        setMainImagePreview({
          isExisting: true,
          url: editData.main_image[0].file_url,
          file_seq: editData.main_image[0].file_seq
        });
      }
      
      // 서브 이미지 데이터 처리 (sub_images 배열 사용)
      if (editData.sub_images && editData.sub_images.length > 0) {
        const subImageData = editData.sub_images.map((image) => ({
          isExisting: true,
          url: image.file_url,
          file_seq: image.file_seq
        }));
        setSubImagesPreview(subImageData);
      }
      
      // 첨부 파일 처리 (files 배열 사용)
      if (editData.files && editData.files.length > 0) {
        const newAttachFileNames = ['첨부 파일', '첨부 파일'];
        editData.files.forEach((file, index) => {
          if (index < 2) {
            newAttachFileNames[index] = file.org_filename || '첨부 파일';
          }
        });
        setAttachFileNames(newAttachFileNames);
      }
      
    } else if (isEdit && apply_seq) {
      // location.state에 데이터가 없으면 MAKING_SUPPORT_APPLY_DETAIL_URL API 호출
      const fetchDetailData = async () => {
        try {
          const response = await instance.post(MAKING_SUPPORT_APPLY_DETAIL_URL, {
            apply_seq: apply_seq
          });
          
          const detailData = response.data.data;
          const files = response.data.data.files || [];
          
          // 폼 데이터 설정
          setFormData({
            apply_phone: detailData.apply_phone || '',
            apply_email: detailData.apply_email || '',
            apply_company: detailData.apply_company || '',
            apply_director: detailData.apply_director || '',
            apply_cinematographer: detailData.apply_cinematographer || '',
            apply_workname: detailData.apply_workname || '',
            apply_workcontents: detailData.apply_workcontents || '',
            apply_contents: detailData.apply_contents || '',
            youtube_url: detailData.youtube_url || '',
            ucc_url: detailData.ucc_url || '',
          });
          
          // 기존 파일들 분류
          const thumbnailFiles = files.filter(file => file.file_type === 'thumbnail');
          const imageFiles = files.filter(file => file.file_type === 'upload_images');
          const attachmentFiles = files.filter(file => file.file_type === 'userfiles');
          
          // 대표 이미지 설정
          if (thumbnailFiles.length > 0) {
            setMainImagePreview({
              isExisting: true,
              url: thumbnailFiles[0].file_url,
              file_seq: thumbnailFiles[0].file_seq
            });
          }
          
          // 서브 이미지 설정
          if (imageFiles.length > 0) {
            const subImageData = imageFiles.map(file => ({
              isExisting: true,
              url: file.file_url,
              file_seq: file.file_seq
            }));
            setSubImagesPreview(subImageData);
          }
          
          // 첨부 파일 설정
          if (attachmentFiles.length > 0) {
            const newAttachFileNames = ['첨부 파일', '첨부 파일'];
            attachmentFiles.forEach((file, index) => {
              if (index < 2) {
                newAttachFileNames[index] = file.org_filename || file.file_name;
              }
            });
            setAttachFileNames(newAttachFileNames);
          }
          
        } catch (error) {
          console.error("제작지원 상세 데이터 가져오기 실패:", error);
          setIsPopupOpen("데이터를 불러오는데 실패했습니다.");
        }
      };
      
      fetchDetailData();
    }
  }, [isEdit, apply_seq, editData]);
  
  // 입력 필드 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // 메인 이미지 업로드 핸들러
  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // 기존 미리보기 URL 해제
    if (mainImagePreview && !mainImagePreview.isExisting) {
      URL.revokeObjectURL(mainImagePreview.url);
    }
    
    setUploadMainImage(file);
    setMainImagePreview({
      isExisting: false,
      file,
      url: URL.createObjectURL(file)
    });
    e.target.value = null;
  };
  
  // 메인 이미지 삭제 핸들러
  const handleMainImageDelete = () => {
    if (mainImagePreview && !mainImagePreview.isExisting) {
      URL.revokeObjectURL(mainImagePreview.url);
    }
    
    setUploadMainImage(null);
    setMainImagePreview(null);
  };
  
  // 서브 이미지 업로드 핸들러
  const handleSubImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // 최대 4개까지만 허용
    const remainingSlots = 4 - subImagesPreview.length;
    const selectedFiles = files.slice(0, remainingSlots);
    
    if (selectedFiles.length === 0) return;
    
    // 새 이미지를 업로드 배열에 추가
    setUploadSubImages(prev => [...prev, ...selectedFiles]);
    
    // 미리보기 이미지 추가
    const newPreviewImages = selectedFiles.map(file => ({
      isExisting: false,
      file,
      url: URL.createObjectURL(file)
    }));
    
    setSubImagesPreview(prev => [...prev, ...newPreviewImages]);
    e.target.value = null;
  };
  
  // 서브 이미지 삭제 핸들러
  const handleSubImageDelete = (index) => {
    const imageToDelete = subImagesPreview[index];
    
    if (imageToDelete.isExisting) {
      // 기존 이미지인 경우 - 미리보기에서만 제거
      setSubImagesPreview(prev => prev.filter((_, i) => i !== index));
    } else {
      // 새로 추가한 이미지인 경우
      URL.revokeObjectURL(imageToDelete.url);
      
      // 새 이미지 배열에서 해당 파일 찾아서 제거
      const newImageIndex = subImagesPreview
        .filter(img => !img.isExisting)
        .findIndex(img => img.file === imageToDelete.file);
      
      if (newImageIndex !== -1) {
        setUploadSubImages(prev => prev.filter((_, i) => i !== newImageIndex));
      }
      
      setSubImagesPreview(prev => prev.filter((_, i) => i !== index));
    }
  };
  
  // 첨부 파일 변경 핸들러
  const handleAttachFileChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const newAttachFiles = [...attachFiles];
    newAttachFiles[index] = file;
    setAttachFiles(newAttachFiles);
    
    const newAttachFileNames = [...attachFileNames];
    newAttachFileNames[index] = file.name;
    setAttachFileNames(newAttachFileNames);
    e.target.value = null;
  };
  
  // 취소 버튼 핸들러
  const handleCancel = () => {
    if (isFromMyPage) {
      navigate('/myPage/support');
    } else {
      navigate('/community/support');
    }
  };
  
  // 신청/수정 버튼 핸들러
  const handleSubmit = async () => {
    // 필수 필드 검증
    const requiredFields = [
      'apply_phone', 'apply_email', 'apply_company', 'apply_director', 
      'apply_cinematographer', 'apply_workname', 'apply_workcontents', 'apply_contents'
    ];
    
    for (const field of requiredFields) {
      if (!formData[field]) {
        setIsPopupOpen(`${field.replace('apply_', '')} 필드는 필수입니다.`);
        return;
      }
    }
    
    // 신규 등록일 때만 이미지 필수 검증
    if (!isEdit) {
      // 대표 이미지 필수 검증 (신규 등록시에만)
      if (!mainImagePreview) {
        setIsPopupOpen(t('representative_image_required'));
        return;
      }
      
      // 서브 이미지 필수 검증 (신규 등록시에만)
      if (subImagesPreview.length === 0) {
        setIsPopupOpen(t('sub_image_required'));
        return;
      }
    }
    
    try {
      const submitFormData = new FormData();
      
      // 수정 모드일 경우 apply_seq 추가
      if (isEdit) {
        submitFormData.append('apply_seq', apply_seq);
        
        // 삭제된 파일 ID 수집
        const deletedFileIds = [];
        
        // 대표 이미지 삭제 확인
        if (editData?.main_image && editData.main_image.length > 0) {
          if (!mainImagePreview?.isExisting) {
            // 기존 대표 이미지가 삭제된 경우
            deletedFileIds.push(editData.main_image[0].file_seq);
          }
        }
        
        // 서브 이미지 삭제 확인
        if (editData?.sub_images && editData.sub_images.length > 0) {
          const existingSubFileSeqs = subImagesPreview
            .filter(img => img.isExisting)
            .map(img => img.file_seq);
          
          editData.sub_images.forEach((originalImage) => {
            if (!existingSubFileSeqs.includes(originalImage.file_seq)) {
              // 기존 서브 이미지가 삭제된 경우
              deletedFileIds.push(originalImage.file_seq);
            }
          });
        }
        
        // 삭제된 파일 ID 전송
        if (deletedFileIds.length > 0) {
          deletedFileIds.forEach(id => {
            submitFormData.append('delete_file_id[]', id);
          });
        }
      }
      
      // 폼 데이터 추가
      Object.entries(formData).forEach(([key, value]) => {
        submitFormData.append(key, value);
      });
      
      // 대표 이미지 추가 (새로운 이미지가 있을 때만)
      if (uploadMainImage) {
        submitFormData.append('thumbnail[]', uploadMainImage);
      }
      
      // 서브 이미지 추가 (새로운 이미지들만)
      uploadSubImages.forEach(file => {
        submitFormData.append('upload_images[]', file);
      });
      
      // 첨부 파일 추가
      attachFiles.forEach(file => {
        if (file) {
          submitFormData.append('userfiles[]', file);
        }
      });
      
      // API URL 결정
      const apiUrl = isEdit ? MAKING_SUPPORT_EDIT_URL : MAKING_SUPPORT_REGISTER_URL;
      
      const response = await instance.post(apiUrl, submitFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setIsPopupOpen({
          message: isEdit ? t('production_support_modified') : t('production_support_applied'),
          action: 'navigate_list'
        });
      } else {
        setIsPopupOpen(isEdit ? '제작지원 수정에 실패했습니다.' : '제작지원 신청에 실패했습니다.');
      }
    } catch (error) {
      console.error(isEdit ? '제작지원 수정 실패:' : '제작지원 신청 실패:', error);
      setIsPopupOpen(isEdit ? '제작지원 수정 중 오류가 발생했습니다.' : '제작지원 신청 중 오류가 발생했습니다.');
    }
  };
  
  // 팝업 확인 버튼 핸들러
  const handleConfirm = () => {
    if (isPopupOpen.action === 'navigate_list') {
      if (isFromMyPage) {
        navigate('/myPage/support');
      } else {
        navigate('/community/support');
      }
    }
    setIsPopupOpen(false);
  };

  return(
    <div className="board_container order_inner">
      <h3 className="commu_title noline">{isEdit ? t('production_support') + ' ' + t('modify') : t('production_support')}</h3>
      <div className="order_title end">
        <div className="b1_1">
          <span className="reset_text_btn">* </span>{t('required_fields')}
        </div>
      </div>

      <ul className="default_info_table board_list quote_table support_form">
        <li className="info_item user_id">
          <div className="label">
            <label htmlFor="userid">{t('id')} <span className="yellow">*</span></label>
          </div>
          <div className="form_info default_input">
            <input
              type="text"
              className="readonly"
              id="userid"
              value={myInfo?.userid}
              readOnly
            />
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="apply_phone">{t('phone_number')} <span className="yellow">*</span></label>
          </div>
          <div className="form_info default_input">
            <input
              type="text"
              name="apply_phone"
              id="apply_phone"
              value={formData.apply_phone}
              onChange={handleInputChange}
            />
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="apply_email">{t('email')} <span className="yellow">*</span></label>
          </div>
          <div className="form_info default_input">
            <input
              type="text"
              name="apply_email"
              id="apply_email"
              value={formData.apply_email}
              onChange={handleInputChange}
            />
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="apply_company">{t('company_name')} <span className="yellow">*</span></label>
          </div>
          <div className="form_info default_input">
            <input
              type="text"
              name="apply_company"
              id="apply_company"
              value={formData.apply_company}
              onChange={handleInputChange}
            />
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="apply_director">{t('director')} <span className="yellow">*</span></label>
          </div>
          <div className="form_info default_input">
            <input
              type="text"
              id="apply_director"
              name="apply_director"
              value={formData.apply_director}
              onChange={handleInputChange}
            />
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="apply_cinematographer">{t('cinematographer')} <span className="yellow">*</span></label>
          </div>
          <div className="form_info default_input">
            <input
              type="text"
              name="apply_cinematographer"
              id="apply_cinematographer"
              value={formData.apply_cinematographer}
              onChange={handleInputChange}
            />
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="apply_workname">{t('work_name')} <span className="yellow">*</span></label>
          </div>
          <div className="form_info default_input">
            <input
              type="text"
              name="apply_workname"
              id="apply_workname"
              value={formData.apply_workname}
              onChange={handleInputChange}
            />
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="apply_workcontents">{t('work_description')} <span className="yellow">*</span></label>
          </div>
          <div className="form_info default_input">
            <textarea
              name="apply_workcontents"
              id="apply_workcontents"
              placeholder={t('work_description_txt')}
              value={formData.apply_workcontents}
              onChange={handleInputChange}
            />
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="apply_contents">{t('application_content')} <span className="yellow">*</span></label>
          </div>
          <div className="form_info default_input">
            <textarea
              name="apply_contents"
              id="apply_contents"
              placeholder={t('apply_content')}
              value={formData.apply_contents}
              onChange={handleInputChange}
            />
          </div>
        </li>

        <li className="info_item file_form">
          <div className="label">
            <span>{t('representative_image')}</span>
            <span className="yellow"> *</span>
          </div>
          <div className="form_info flex-wrap">
            <div className="attach_file">
              <div className="img_attach_box">
                {!mainImagePreview && (
                  <label
                    htmlFor="main_img_attach"
                    className="default_btn ic_lg white attach_btn"
                  />
                )}
                <input
                  type="file"
                  className="i_hide"
                  name="img_file"
                  id="main_img_attach"
                  accept=".png,.jpg"
                  ref={mainImageInputRef}
                  onChange={handleMainImageChange}
                />
                <ul className="preview_list">
                  {mainImagePreview && (
                    <li className="default_btn ic_lg">
                      <img
                        className="object-cover"
                        src={mainImagePreview.url}
                        alt="대표 이미지"
                        loading="lazy"
                      />
                      <button
                        type="button"
                        className="delete_btn"
                        onClick={handleMainImageDelete}
                      />
                    </li>
                  )}
                </ul>
              </div>
              <p className="dash_list flex-2 column w-100 commu_img">
                <span>{t('file_capacity_limit')}</span>
                <span>{t('maximum_1_file')}</span>
              </p>
            </div>
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <span>{t('sub_image')}</span>
            <span className="yellow"> *</span>
          </div>
          <div className="form_info flex-wrap">
            <div className="attach_file">
              <div className="img_attach_box">
                {subImagesPreview.length < 4 && (
                  <label
                    htmlFor="sub_img_attach"
                    className="default_btn ic_lg white attach_btn"
                  />
                )}
                <input
                  type="file"
                  className="i_hide"
                  id="sub_img_attach"
                  accept=".png,.jpg"
                  multiple
                  name="img_file"
                  ref={subImageInputRef}
                  onChange={handleSubImagesChange}
                />
                <ul className="preview_list">
                  {subImagesPreview.map((image, index) => (
                    <li key={index} className="default_btn ic_lg">
                      <img
                        className="object-cover"
                        src={image.url}
                        alt={`서브 이미지 ${index + 1}`}
                        loading="lazy"
                      />
                      <button
                        type="button"
                        className="delete_btn"
                        onClick={() => handleSubImageDelete(index)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
              <p className="dash_list flex-2 column w-100 commu_img">
                <span>{t('file_capacity_limit')}</span>
                <span>{t('maximum_4_files')}</span>
              </p>
            </div>
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="youtube_url">YouTube</label>
          </div>
          <div className="form_info default_input">
            <input
              type="text"
              name="youtube_url"
              id="youtube_url"
              value={formData.youtube_url}
              onChange={handleInputChange}
            />
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="ucc_url">UCC URL</label>
          </div>
          <div className="form_info default_input">
            <input
              type="text"
              name="ucc_url"
              id="ucc_url"
              value={formData.ucc_url}
              onChange={handleInputChange}
            />
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <span>{t('file_attachment')}</span>
          </div>
          <div className="form_info flex-wrap">
            <div className=" w-100">
              <div className="flex-8 column">
                <div className="default_input btn_group_4">
                  <input
                    id="file_attachment1"
                    accept="image/*, .pdf"
                    type="file"
                    className="i_hide"
                    ref={fileInput1Ref}
                    onChange={(e) => handleAttachFileChange(0, e)}
                  />
                  <span className="form_input placeholder">
                    <span className="ellipsis_clamp">{attachFileNames[0]}</span>
                  </span>
                  <label
                    className="default_btn sm light_gray"
                    htmlFor="file_attachment1"
                  >
                    {t('attached_file')}
                  </label>
                </div>

                <div className="default_input btn_group_4">
                  <input
                    id="file_attachment2"
                    accept="image/*, .pdf"
                    type="file"
                    className="i_hide"
                    ref={fileInput2Ref}
                    onChange={(e) => handleAttachFileChange(1, e)}
                  />
                  <span className="form_input placeholder">
                    <span className="ellipsis_clamp">{attachFileNames[1]}</span>
                  </span>
                  <label
                    className="default_btn sm light_gray"
                    htmlFor="file_attachment2"
                  >
                    {t('attached_file')}
                  </label>
                </div>
              </div>
              
              <p className="dash_list flex-2 column w-100 commu_img">
                <span>
                  {t('file_capacity_limit')}
                </span>
                <span>{t('maximum_2_files')}</span>
              </p>
            </div>
          </div>
        </li>
      </ul>

      <div className="submit_box bg mt_32">
        <button
          className="default_btn white lg"
          onClick={handleCancel}
        >{t('cancel')}
        </button>
        <button
          className="default_btn black lg"
          onClick={handleSubmit}
        >{isEdit ? t('modify') : t('apply')}
        </button>
      </div>
      
      <PopupLoginError isPopup={isPopupOpen} setIsPopup={setIsPopupOpen} onConfirm={handleConfirm}/>
    </div>
  )
};

export default SupportForm;